import React, { createContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAccount, useChainId } from 'wagmi';
import { useEthersSigner } from '../../hooks/useEthersSigner';

import config from '../../config';
import TombFinance from '../../tomb-finance';

export interface TombFinanceContext {
  tombFinance?: TombFinance;
}

export const Context = createContext<TombFinanceContext>({ tombFinance: null });

const KAVA_CHAIN_ID = 2222;

export const TombFinanceProvider = ({ children }: { children: React.ReactNode }) => {
  const chainId = useChainId();
  const { address: account, isConnected } = useAccount();
  const signer = useEthersSigner();
  const [tombFinance, setTombFinance] = useState<TombFinance>();
  const isInitialized = useRef(false);
  const lastAccount = useRef<string | undefined>();
  
  useEffect(() => {
    // Only create TombFinance instance when on correct chain and not yet initialized
    if (chainId !== KAVA_CHAIN_ID) {
      if (tombFinance) {
        console.log('Not on Kava chain, TombFinance disabled');
        setTombFinance(undefined);
        isInitialized.current = false;
        lastAccount.current = undefined;
      }
      return;
    }

    if (!isInitialized.current) {
      console.log('Creating TombFinance instance for Kava');
      const tomb = new TombFinance(config[KAVA_CHAIN_ID]);
      setTombFinance(tomb);
      isInitialized.current = true;
    }
  }, [chainId]);

  // Connect wallet signer when wallet is connected
  useEffect(() => {
    if (tombFinance && isConnected && account && signer) {
      // Only unlock if account changed or wasn't unlocked before
      if (lastAccount.current !== account) {
        console.log('🔐 Connecting signer to TombFinance contracts...');
        tombFinance.unlockWallet(signer, account);
        lastAccount.current = account;
      }
    } else if (tombFinance && (!isConnected || !account)) {
      // Wallet disconnected - keep TombFinance but mark as locked
      if (lastAccount.current) {
        console.log('🔒 Wallet disconnected');
        lastAccount.current = undefined;
      }
    }
  }, [tombFinance, isConnected, account, signer]);

  const value = useMemo(() => ({ tombFinance }), [tombFinance]);

  return <Context.Provider value={value}>{children}</Context.Provider>;
};
